import React, { useState } from "react";
import { FormField, Input, NativeSelect } from "../../ui";

const OTHER = "__other__";

/**
 * EducationFields — degree, the branch under it, the college and the aggregate.
 *
 * The degree list is the server's catalogue (useProfileOptions). A value stored
 * before the catalogue existed stays selectable so a saved row never loses what
 * it held; "Other" opens a typed box that writes `degree` itself. A degree with
 * branches offers them (plus "Other" → a typed branch); one without shows no
 * branch field.
 *
 * Value-first: `value` is { degree, degreeBranch, college, percentage } and
 * `onChange` gets a patch. Controls are the library's; the caller lays them in
 * its own grid through `fieldClassName`.
 */
const EducationFields = ({ value = {}, onChange, degrees = [], size = "md", fieldClassName = "" }) => {
  const degree = value.degree || "";
  const branch = value.degreeBranch || "";
  const known = degrees.find((d) => d.label === degree) || null;
  const branches = known?.branches || [];
  // "Other" is a mode the person chose, not a value — a stored degree the
  // catalogue does not list is shown as itself, never as "Other".
  const [otherDegree, setOtherDegree] = useState(false);
  const [otherBranch, setOtherBranch] = useState(false);
  const branchListed = !branch || branches.includes(branch);

  const pickDegree = (e) => {
    const next = e.target.value;
    setOtherDegree(next === OTHER);
    setOtherBranch(false);
    onChange({ degree: next === OTHER ? "" : next, degreeBranch: "" });
  };

  const pickBranch = (e) => {
    const next = e.target.value;
    setOtherBranch(next === OTHER);
    onChange({ degreeBranch: next === OTHER ? "" : next });
  };

  const showOtherBranch = branches.length > 0 && (otherBranch || !branchListed);

  return (
    <>
      <FormField label="Degree type" className={fieldClassName}>
        <NativeSelect size={size} value={otherDegree ? OTHER : degree} onChange={pickDegree}>
          <option value="">Select degree</option>
          {!known && degree && <option value={degree}>{degree}</option>}
          {degrees.map((d) => <option key={d.key} value={d.label}>{d.label}</option>)}
          <option value={OTHER}>Other (type it)</option>
        </NativeSelect>
      </FormField>

      {otherDegree && (
        <FormField label="Degree name" className={fieldClassName}>
          <Input size={size} value={degree} onChange={(e) => onChange({ degree: e.target.value })} placeholder="e.g. B.Des" autoFocus />
        </FormField>
      )}

      {branches.length > 0 && (
        <FormField label="Branch / specialisation" className={fieldClassName}>
          <NativeSelect size={size} value={showOtherBranch ? OTHER : branch} onChange={pickBranch}>
            <option value="">Select branch</option>
            {branches.map((b) => <option key={b} value={b}>{b}</option>)}
            <option value={OTHER}>Other (type it)</option>
          </NativeSelect>
        </FormField>
      )}

      {showOtherBranch && (
        <FormField label="Branch name" className={fieldClassName}>
          <Input size={size} value={branch} onChange={(e) => onChange({ degreeBranch: e.target.value })} placeholder="Type the branch" />
        </FormField>
      )}

      <FormField label="College / University" className={fieldClassName}>
        <Input size={size} value={value.college || ""} onChange={(e) => onChange({ college: e.target.value })} placeholder="e.g. JNTU Hyderabad" />
      </FormField>

      <FormField label="Percentage / aggregate" className={fieldClassName} hint="Out of 100">
        <Input
          size={size}
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step="0.01"
          value={value.percentage ?? ""}
          onChange={(e) => onChange({ percentage: e.target.value })}
          placeholder="e.g. 72.5"
        />
      </FormField>
    </>
  );
};

export default EducationFields;
